<template>
    <Modal
        v-if="showShareModal"
        :allowClose="true"
        @modal:close="resumeShareStore.hideShareModal"
        maxWidth="max-w-[540px]"
        :preventScrolling="false"
    >
        <template #body>
            <h2 class="text-[24px] leading-[36px] text-won-blue font-semibold">
                Share {{ formatedResumeTitle }}
            </h2>
            
            <div class="mt-8">
                <label class="text-won-blue font-bold text-[14px] leading-[20px]">
                    Add emails and hit Enter/Return
                </label>

                <div
                    class="
                        p-2.5 text-base text-won-black rounded border border-[#DBDBDC] w-full
                        mt-3 flex flex-wrap gap-2 cursor-text
                    "
                    @click="focusOnInputHandler"
                >
                    <div
                        class="border border-[#C2C4C6] py-1 px-1.5 rounded-full flex gap-1 items-center w-fit"
                        v-for="(email, index) in selectedUsers"
                    >
                        <span
                            class="text-[12px] leading-[15px] text-won-blue"
                        >
                            {{ email }}
                        </span>
                        
                        <span
                            @click="() => resumeShareStore.removeEmailHandler(index)"
                            class="cursor-pointer"
                        >
                            <XMarkIcon class="size-4"/>
                        </span>
                    </div>
                    
                    <input
                        v-model="email"
                        placeholder="johndoe@gmail.com"
                        @keydown.enter="resumeShareStore.addEmailHandler"
                        @change="resumeShareStore.emailInputChangeHandler"
                        @blur="resumeShareStore.addEmailHandler"
                        ref="emailInputRef"
                        class="
                            outline-none focus:ring-0 placeholder:text-[#686E77] p-0 grow
                            focus:outline-none focus:border-won-green border-none
                        "
                    />
                </div>
                
                <div class="h-4 mt-1">
                    <span class="text-red-500 text-small block">
                        {{ validationErrors.email }}
                    </span>
                </div>
            </div>

            <div class="mt-4" v-if="selectedUsers.length > 0">
                <RadioGroup
                    v-model="invitedUsersAccessRights"
                    class="flex gap-4"
                >
                    <RadioGroupOption
                        :value="option.value"
                        v-for="option in ACCESS_MENU_ACTIONS"
                        v-slot="{ checked }"
                        :disabled="option?.disabled"
                    >
                        <div
                            class="flex items-center gap-2 shadow-radio-card px-6 py-3 text-won-dark-blue border rounded text-center"
                            :class="[
                                checked ? 'border-won-green bg-won-green-light' : 'border-[rgba(95, 95, 95, 0.15)]  bg-white',
                                option?.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            ]"
                        >
                            <div
                                class="size-4 rounded-full flex justify-center items-center"
                                :class="checked ? 'bg-won-green' : 'border border-[#D8D8E9]'" 
                            >
                                <div class="size-1.5 rounded-full bg-white" v-if="checked"></div>
                            </div>
                            
                            <span class="text-[15px] leading-[24px] font-semibold">
                                {{ option.actionText }}
                            </span>
                        </div>
                    </RadioGroupOption>
                </RadioGroup>
            </div>
            
            <div class="mt-4" v-else-if="sharedWith.length > 0">
                <h3 class="text-[14px] leading-[20px] text-won-blue font-semibold">
                    People with access
                </h3>
                
                <div
                    class="text-small text-won-dark block mt-3"
                    v-if="selectedUsers.length === 0"
                >
                    Add people using email address
                </div>
                
                <div class="mt-3 flex flex-col gap-4 max-h-[200px] h-full overflow-y-auto pr-2">
                    <AccessPreview
                        v-for="user in sharedWith"
                        :userId="user?.id"
                        :userEmail="user.email"
                        :userName="user?.name || '-'"
                        :permission="user?.permission"
                        :options="ACCESS_MENU_ACTIONS"
                        :displayMap="USER_PERMISSION_DISPLAY_MAP"
                    />
                </div>
            </div>
            
            <div class="mt-4" v-if="selectedUsers.length === 0">
                <h3
                    class="text-[14px] leading-[20px] text-won-blue font-semibold"
                >
                    General access
                </h3>
                
                <div class="mt-3">
                    <div class="flex justify-between items-center max-h-9">
                        <div class="flex items-center gap-3">
                            <div class="rounded-full bg-won-sand-light p-1.5 size-9">
                                <GlobeAltIcon
                                    class="size-6 text-won-dark"
                                />
                            </div>
                            
                            <div class="">
                                <span class="text-base text-won-dark-blue">
                                    <AccessPreviewMenu
                                        :options="GENERAL_ACCESS_OPTIONS"
                                        :displayMap="GENERAL_PERMISSION_DISPLAY_MAP"
                                        @update:model-value="resumeShareStore.generalAccessChangeHandler"
                                        v-model="generalAccessValue"
                                    />
                                </span>
                
                                <span
                                    class="text-small text-won-dark block mt-1"
                                >
                                    <span v-if="generalAccessValue === 'restricted'">
                                        Only people with access can open with the link
                                    </span>
                                    <span v-if="generalAccessValue === 'anyoneWithLink'">
                                        Anyone on the internet with the link can view
                                    </span>
                                </span>
                            </div>
                        </div>

                        <AccessPreviewMenu
                            v-if="generalAccessValue === 'anyoneWithLink'"
                            :options="ACCESS_MENU_ACTIONS"
                            :displayMap="USER_PERMISSION_DISPLAY_MAP"
                            v-model="anyoneWithLinkAccessLevel"
                        />
                    </div>
                </div>
            </div>
            
            <div class="mt-14 text-base font-medium flex justify-end items-stretch gap-5">
                <CopyShareLinkBtn
                    :disabled="loadingCopyShareLink || loadingShareResume"
                />
                
                <button
                    class="
                        bg-won-green rounded px-10 py-1.5 border border-won-green hover:opacity-90 
                        disabled:border-won-gray disabled:opacity-40 disabled:bg-won-gray
                        flex justify-center items-center relative
                    "
                    @click="resumeShareStore.shareResumeWithEmails"
                    :disabled="loadingShareResume || loadingCopyShareLink"
                >
                    <span
                        class="text-white"
                        :class="{'opacity-0' : loadingShareResume && !loadingCopyShareLink}"
                    >
                        <span v-if="selectedUsers.length > 0">Share</span>
                        <span v-else>Done</span>
                    </span>

                    <span class="absolute" v-if="!loadingCopyShareLink">
                        <LoadingIcon
                            :class="{'opacity-0' : !loadingShareResume && !loadingCopyShareLink}"
                        />
                    </span>
                </button>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { toRefs, computed } from "vue";
import { storeToRefs } from "pinia";
import { useResumeShare } from "../../store/share-resume.store";
import { useNotifications } from "../../store/notifications.store";

import {
    USER_PERMISSION_DISPLAY_MAP,
    ACCESS_MENU_ACTIONS,
    GENERAL_PERMISSION_DISPLAY_MAP,
    GENERAL_ACCESS_OPTIONS
} from "../../utils/share-resume.storage"

import Modal from "../../components/modal.vue";
import Label from "../../components/label.vue";
import { RadioGroup, RadioGroupOption } from "@headlessui/vue";
import AccessPreview from "../../components/resume-tool/access-preview.vue";
import AccessPreviewMenu from "../../components/resume-tool/access-preview-menu.vue";
import CopyShareLinkBtn from "../../components/resume-tool/copy-share-link-btn.vue";

import { XMarkIcon, GlobeAltIcon } from "@heroicons/vue/24/outline"
import LoadingIcon from "../../icons/loading-icon.vue";

const props = defineProps({
    resume: {
        type: Object,
        default: null
    },
    resumeTitle: {
        type: String,
        default: ''
    }
})

const { resume, resumeTitle } = toRefs(props);

const resumeShareStore = useResumeShare();
const notificationsStore = useNotifications();
const {
    email,
    sharedWith,
    emailInputRef,
    selectedUsers,
    showShareModal,
    validationErrors,
    loadingShareResume,
    generalAccessValue,
    loadingCopyShareLink,
    invitedUsersAccessRights,
    anyoneWithLinkAccessLevel,
} = storeToRefs(resumeShareStore);

const focusOnInputHandler = () => {
    emailInputRef.value.focus();
}

const formatedResumeTitle = computed(() => {
    return `"${resumeTitle.value?.replace('<b>', '')?.replace("</b>", '')}"`
})
</script>
